import type { FC } from 'react';
import { TouchableOpacity, ActivityIndicator } from 'react-native';
import { useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faSquareCheck } from '@fortawesome/pro-light-svg-icons/faSquareCheck';
import { faSquareX } from '@fortawesome/pro-light-svg-icons/faSquareX';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faSquareExclamation } from '@fortawesome/pro-light-svg-icons/faSquareExclamation';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { graphql, type ExtractEnum } from 'graphql-schema';
import { Colors, voloGreys } from '../../../theme';
import { ErrorMessageEnum } from '../../../constants/enums';
import { View, Text } from '../../../base-components';
import styles from './styles';
import type { RosterItemProps } from '../RosterItem/RosterItem';
import { TEAM_ROSTER_RSVPS_FRAGMENT } from '../graphql/TEAM_ROSTER';
import { GAME_ROSTER_QUERY } from '../../GameRoster';

export const ICON_SIZE = 20;
const { no, yes } = Colors.accents || {};

const getRsvpIcon = (response?: ExtractEnum<'rsvp_response_enum'>) => {
  if (response === 'YES') return { icon: faSquareCheck, color: yes.toString() };
  if (response === 'NO') return { icon: faSquareX, color: no.toString() };
  return { icon: faSquareExclamation, color: voloGreys.medium.toString() };
};

const RSVP_FOR_PLAYER = graphql(
  `
    mutation RsvpForGame($userId: uuid!, $gameId: uuid!, $response: RSVPResponse!) {
      rsvpForGame(user: $userId, game: $gameId, response: $response) {
        gameTeamByGameTeam {
          ...TeamRosterRsvps
        }
      }
    }
  `,
  [TEAM_ROSTER_RSVPS_FRAGMENT]
);

type RsvpSelectorProps = Pick<
  RosterItemProps,
  | 'hasCaptainPermissions'
  | 'playerId'
  | 'response'
  | 'setError'
  | 'setSuccess'
  | 'teamId'
  | 'refetch'
> & { gameId: string };

const RsvpSelector: FC<RsvpSelectorProps> = ({
  gameId,
  hasCaptainPermissions,
  playerId,
  response,
  refetch,
  setError,
  setSuccess,
}) => {
  const [rsvpForPlayer, { loading, error }] = useMutation(RSVP_FOR_PLAYER, {
    refetchQueries: [GAME_ROSTER_QUERY],
    update: cache => {
      // Refetch mobile dash to make sure rsvps are up to date
      cache.evict({ fieldName: 'dashboard_schedule' });
      cache.gc();
    },
  });

  const handleRsvpForPlayer = async (response: ExtractEnum<'rsvp_response_enum'>) => {
    try {
      await rsvpForPlayer({
        variables: {
          gameId,
          response,
          userId: playerId,
        },
      });
      await refetch?.();
      setSuccess?.('Player RSVP updated');
    } catch (error) {
      setError?.(ErrorMessageEnum.RSVP_FOR_PLAYER);
    }
  };

  if (error) {
    return (
      <View>
        <Text>{String(error)}</Text>
      </View>
    );
  }
  if (loading) return <ActivityIndicator />;

  const { icon, color } = getRsvpIcon(response);

  return (
    <View>
      {hasCaptainPermissions && !response ? (
        <View style={styles.captainContainer}>
          <TouchableOpacity onPress={() => handleRsvpForPlayer('YES')}>
            <FontAwesomeIcon
              icon={faCheck}
              color={voloGreys.medium.toString()}
              size={ICON_SIZE}
              style={styles.icon}
            />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => handleRsvpForPlayer('NO')}>
            <FontAwesomeIcon
              icon={faXmark}
              color={voloGreys.medium.toString()}
              size={ICON_SIZE}
              style={styles.icon}
            />
          </TouchableOpacity>
        </View>
      ) : (
        <FontAwesomeIcon icon={icon} color={color} size={ICON_SIZE} style={styles.icon} />
      )}
    </View>
  );
};

export default RsvpSelector;
